import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonControl,
  IconControl,
  RadioButtonListControl,
  SidePanelFormControl,
  TableControl,
  ToastNotifications,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import { SystemTypes } from 'spark-core-dx/services';
import AIPanel from '../../components/AIChat/AIPanel';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ConvokeDialogMessage from '../../components/convoke/ConvokeDialogMessage';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import {
  activityQueryFamilyKey,
  activitySourceReference,
  buildActivityTableSearch,
  buildActivityWorkspaceRequest,
  CLIENT_ACTIVITY_EXPORT_FORMATS,
  CLIENT_ACTIVITY_ITEM_NAMES,
  CLIENT_ACTIVITY_OPERATIONS,
  createDefaultActivitySearch,
  decodeActivityExport,
  normalizeActivityAIFilter,
  runActivityOperation,
  runActivitySearchOperation,
  selectActivityMetadata,
} from './clientActivityContracts';
import { createClientCommandRequestGate } from './clientCommandRequestGate';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

const SEARCH_FORM_FIELDS = new Set([
  'fromutc',
  'toutc',
  'text',
  'code',
  'codes',
  'source',
  'sources',
  'status',
  'statuses',
  'actor',
  'principal',
  'provider',
  'correlationid',
  'targetrecord',
]);

const metadataFields = (metadata) => (
  metadata?.Fields ?? metadata?.fields ?? []
);

const ACTIVITY_SOURCE_OPTIONS = [
  { Text: 'Event', Value: 'Event' },
  { Text: 'Status Log', Value: 'StatusLog' },
  { Text: 'Log', Value: 'Log' },
  {
    Text: 'Client Resource Activity',
    Value: 'ClientResourceActivity',
  },
];

const ACTIVITY_CODE_OPTIONS_OPERATION = 'SelectDataClientActivityCodes';

const ACTIVITY_EXPORT_FORMAT_METADATA = Object.freeze({
  FieldName: 'ActivityExportFormat',
  DisplayName: 'Export format',
  DataJson: CLIENT_ACTIVITY_EXPORT_FORMATS,
});

const ACTIVITY_CODE_FALLBACK_OPTIONS = [
  ...Object.values(CLIENT_ACTIVITY_OPERATIONS).map((value) => ({
    Text: value,
    Value: value,
  })),
  { Text: 'Status Log: Status', Value: 'status' },
  { Text: 'Status Log: Warning', Value: 'warning' },
  { Text: 'Status Log: Error', Value: 'error' },
  { Text: 'Status Log: Thinking', Value: 'thinking' },
];

const ACTIVITY_STATUS_OPTIONS = [
  { Text: 'Succeeded', Value: 'Succeeded' },
  { Text: 'Completed', Value: 'Completed' },
  { Text: 'Created', Value: 'Created' },
  { Text: 'In Progress', Value: 'InProgress' },
  { Text: 'Scheduled', Value: 'Scheduled' },
  { Text: 'Paused', Value: 'Paused' },
  { Text: 'Pending', Value: 'Pending' },
  { Text: 'Cancelled', Value: 'Cancelled' },
  { Text: 'Revoked', Value: 'Revoked' },
  { Text: 'Denied', Value: 'Denied' },
  { Text: 'Failed', Value: 'Failed' },
  { Text: 'Status', Value: 'Status' },
  { Text: 'Thinking', Value: 'Thinking' },
  { Text: 'Debug', Value: 'Debug' },
  { Text: 'Info', Value: 'Info' },
  { Text: 'Timing', Value: 'Timing' },
  { Text: 'Warning', Value: 'Warning' },
  { Text: 'Error', Value: 'Error' },
  { Text: 'Deploy Info', Value: 'DeployInfo' },
  { Text: 'Unknown', Value: 'Unknown' },
];

const selectSearchFields = (metadata) => {
  const fields = metadataFields(metadata);
  const availableFieldNames = new Set(fields.map((field) => String(
    field?.FieldName ?? field?.fieldName ?? '',
  ).toLowerCase()));
  return fields
    .filter((field) => {
      const fieldName = String(
        field?.FieldName ?? field?.fieldName ?? '',
      ).toLowerCase();
      if (!SEARCH_FORM_FIELDS.has(fieldName)) return false;
      if (fieldName === 'code' && availableFieldNames.has('codes')) return false;
      if (fieldName === 'source' && availableFieldNames.has('sources')) return false;
      if (fieldName === 'status' && availableFieldNames.has('statuses')) return false;
      return true;
    })
    .map((field) => {
      const fieldName = String(
        field?.FieldName ?? field?.fieldName ?? '',
      ).toLowerCase();
      if (fieldName === 'code' || fieldName === 'codes') {
        return {
          ...field,
          FieldName: 'Codes',
          fieldName: 'Codes',
          FieldType: SystemTypes.UIFieldType.MultiSelectList,
          fieldType: SystemTypes.UIFieldType.MultiSelectList,
          DataOperation: fieldName === 'codes'
            ? (field?.DataOperation ?? ACTIVITY_CODE_OPTIONS_OPERATION)
            : undefined,
          DataJson: fieldName === 'codes'
            ? field?.DataJson
            : ACTIVITY_CODE_FALLBACK_OPTIONS,
        };
      }
      if (fieldName === 'source' || fieldName === 'sources') {
        return {
          ...field,
          FieldName: 'Sources',
          fieldName: 'Sources',
          FieldType: SystemTypes.UIFieldType.MultiSelectList,
          fieldType: SystemTypes.UIFieldType.MultiSelectList,
          DataJson: ACTIVITY_SOURCE_OPTIONS,
        };
      }
      if (fieldName === 'status' || fieldName === 'statuses') {
        return {
          ...field,
          FieldName: 'Statuses',
          fieldName: 'Statuses',
          FieldType: SystemTypes.UIFieldType.MultiSelectList,
          fieldType: SystemTypes.UIFieldType.MultiSelectList,
          DataJson: ACTIVITY_STATUS_OPTIONS,
        };
      }
      return field;
    });
};

const ACTIVITY_FILTER_SUMMARY_FIELDS = [
  ['Text', 'Text'],
  ['Codes', 'Code'],
  ['Sources', 'Source'],
  ['Statuses', 'Status'],
  ['Actor', 'Actor'],
  ['Principal', 'Principal'],
  ['Provider', 'Provider'],
  ['CorrelationId', 'Correlation'],
  ['TargetRecord', 'Target'],
];

const formatActivityDateTime = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const activityFilterSummary = (search) => {
  const filters = ACTIVITY_FILTER_SUMMARY_FIELDS
    .map(([fieldName, label]) => {
      const rawValue = search?.[fieldName];
      const value = Array.isArray(rawValue)
        ? rawValue.map((item) => String(item).trim()).filter(Boolean).join(', ')
        : String(rawValue || '').trim();
      return value ? `${label}: ${value}` : '';
    })
    .filter(Boolean);
  return filters.length ? filters.join(' · ') : 'Default filters';
};

const activitySortSummary = (search) => {
  const sortBy = search?.SortBy === 'TimestampUtc'
    ? 'Timestamp'
    : (search?.SortBy || 'Timestamp');
  const direction = search?.SortDirection || 'Descending';
  return `${sortBy} · ${direction} · ${Number(search?.PageSize || 50)} rows`;
};

const normalizeTableQuery = (query) => ({
  ...query,
  SortBy: !query?.SortBy || query.SortBy === 'CreatedDate'
    ? 'TimestampUtc'
    : query.SortBy,
  SortOrder: String(query?.SortOrder || '').toUpperCase() === 'ASC'
    ? 'ASC'
    : 'DESC',
  Take: Math.min(200, Math.max(1, Number(query?.Take || 50))),
  Skip: Math.max(0, Number(query?.Skip || 0)),
});

const hasGenericGridFilter = (query) => {
  const node = query?.RootFilterNode;
  if (!node) return false;
  if (Array.isArray(node)) return node.length > 0;
  return Object.keys(node).length > 0;
};

const ClientActivityState = ({
  loading,
  loadingText = 'Searching indexed client activity...',
  error,
}) => {
  if (loading) {
    return (
      <ResourceStateControl
        loading
        loadingText={loadingText}
        compact
      />
    );
  }
  if (error) {
    return (
      <ResourceStateControl message={error} tone="error" compact />
    );
  }
  return null;
};

const ClientActivityScreen = ({
  screen,
  clientId,
  currentEnvironment,
  workspaceHeader = null,
}) => {
  const navigate = useNavigate();
  const { canExecute } = useCorePermissions();
  const workspaceManager = useGenericManager(
    CLIENT_ACTIVITY_ITEM_NAMES.workspace,
  );
  const managerRef = useRef(workspaceManager);
  const searchGateRef = useRef(createClientCommandRequestGate());
  const detailGateRef = useRef(createClientCommandRequestGate());
  const sourceGateRef = useRef(createClientCommandRequestGate());
  const exportGateRef = useRef(createClientCommandRequestGate());
  const continuationRef = useRef(new Map());
  const firstResultRef = useRef(null);
  const activeSearchRef = useRef(createDefaultActivitySearch());
  const lastGridSearchRef = useRef(activeSearchRef.current);
  const [searchDraft, setSearchDraft] = useState(activeSearchRef.current);
  const [metadata, setMetadata] = useState({
    activity: null,
    search: null,
    detail: null,
  });
  const [lastResult, setLastResult] = useState(null);
  const [gridGeneration, setGridGeneration] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInfoOpen, setSearchInfoOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [exportState, setExportState] = useState({
    loading: false,
    error: '',
  });
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('');
  const [activityToast, setActivityToast] = useState(null);
  const activityToastSequenceRef = useRef(0);
  const canLoadDetail = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.loadClientActivityDetail,
  );
  const canResolveSourceRoute = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.resolveClientActivitySourceRecordRoute,
  );
  const canExport = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.exportClientActivitySearch,
  );
  const canUseAI = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.aiQuestionClientActivity,
  );

  const showActivityToast = useCallback((message) => {
    activityToastSequenceRef.current += 1;
    setActivityToast({
      id: activityToastSequenceRef.current,
      message,
    });
  }, []);

  useEffect(() => {
    managerRef.current = workspaceManager;
  }, [workspaceManager]);

  const captureMetadata = useCallback((result) => {
    setMetadata((current) => ({
      activity: selectActivityMetadata(result, 'ActivityMetadata')
        || current.activity,
      search: selectActivityMetadata(result, 'SearchMetadata')
        || current.search,
      detail: selectActivityMetadata(result, 'DetailMetadata')
        || current.detail,
    }));
  }, []);

  const executeSearch = useCallback(async (
    search,
    {
      resetGrid = false,
      surface = 'search',
    } = {},
  ) => {
    const scopeKey = `${clientId}:${currentEnvironment}:activity-search`;
    const request = searchGateRef.current.begin(scopeKey);
    const pageSurface = surface === 'initial' || surface === 'refresh';
    if (pageSurface) {
      setPageLoading(true);
      setPageError('');
    } else if (surface === 'search') {
      setSearchBusy(true);
      setSearchError('');
    } else if (surface === 'grid') {
      setSearchError('');
    }
    try {
      const result = await runActivitySearchOperation(
        managerRef.current,
        buildActivityWorkspaceRequest({
          clientId,
          currentEnvironment,
          search,
        }),
        'Client activity search is unavailable.',
      );
      if (!searchGateRef.current.isCurrent(request)) return undefined;
      captureMetadata(result);
      setLastResult(result);
      if (resetGrid) {
        const committed = {
          ...search,
          Continuation: '',
        };
        activeSearchRef.current = committed;
        lastGridSearchRef.current = committed;
        const family = activityQueryFamilyKey(search, search.PageSize);
        continuationRef.current = new Map([
          [family, new Map([
            [0, ''],
            [1, result?.Continuation || ''],
          ])],
        ]);
        firstResultRef.current = {
          family,
          page: 0,
          result,
        };
        setGridGeneration((value) => value + 1);
      }
      return result;
    } catch (exception) {
      if (searchGateRef.current.isCurrent(request)) {
        const failure =
          exception?.message || 'Client activity search is unavailable.';
        if (pageSurface) setPageError(failure);
        else setSearchError(failure);
      }
      return null;
    } finally {
      if (searchGateRef.current.isCurrent(request)) {
        if (pageSurface) setPageLoading(false);
        else if (surface === 'search') setSearchBusy(false);
      }
    }
  }, [
    captureMetadata,
    clientId,
    currentEnvironment,
  ]);

  useEffect(() => {
    const requestGates = [
      searchGateRef.current,
      detailGateRef.current,
      sourceGateRef.current,
      exportGateRef.current,
    ];
    const initial = createDefaultActivitySearch();
    activeSearchRef.current = initial;
    lastGridSearchRef.current = initial;
    setSearchDraft(initial);
    setSearchOpen(false);
    setSearchInfoOpen(false);
    continuationRef.current = new Map();
    firstResultRef.current = null;
    setDetail(null);
    setDetailOpen(false);
    setPageError('');
    setSearchBusy(false);
    setSearchError('');
    setDetailError('');
    setSourceLoading(false);
    setActionError('');
    setExportState({ loading: false, error: '' });
    setExportOpen(false);
    setExportFormat('');
    setActivityToast(null);
    executeSearch(initial, { resetGrid: true, surface: 'initial' });
    return () => requestGates.forEach((gate) => gate.cancel());
  }, [clientId, currentEnvironment, executeSearch]);

  const runBoundedSearch = useCallback(async (submittedDraft) => {
    const draft = submittedDraft || searchDraft || activeSearchRef.current;
    const next = {
      ...draft,
      SortBy: draft.SortBy || 'TimestampUtc',
      SortDirection: draft.SortDirection || 'Descending',
      PageSize: Number(draft.PageSize || 50),
      Skip: 0,
      Continuation: '',
    };
    setSearchDraft(next);
    const result = await executeSearch(next, {
      resetGrid: true,
      surface: 'search',
    });
    if (result) setSearchOpen(false);
    return result;
  }, [executeSearch, searchDraft]);

  const refreshSearch = useCallback(() => {
    const committed = {
      ...activeSearchRef.current,
      Skip: 0,
      Continuation: '',
    };
    setSearchError('');
    return executeSearch(committed, {
      resetGrid: true,
      surface: 'refresh',
    });
  }, [executeSearch]);

  const clearSearch = useCallback(() => {
    const next = createDefaultActivitySearch();
    setSearchDraft(next);
    setSearchOpen(false);
    return executeSearch(next, {
      resetGrid: true,
      surface: 'search',
    });
  }, [executeSearch]);

  const queryGrid = useCallback(async (suppliedQuery) => {
    const query = normalizeTableQuery(suppliedQuery);
    if (hasGenericGridFilter(query)) {
      setSearchError(
        'Use the bounded Activity Search fields; generic grid filters are disabled.',
      );
      return { Items: [], TotalItemCount: 0, mode: 'replace' };
    }
    const page = Math.floor(query.Skip / query.Take);
    const cached = firstResultRef.current;
    const useCachedInitialSearch = cached
      && page === 0
      && query.SortBy === 'TimestampUtc'
      && query.SortOrder === 'DESC';
    if (cached && page === 0 && !useCachedInitialSearch) {
      firstResultRef.current = null;
    }
    const tableSearch = useCachedInitialSearch
      ? {
        ...activeSearchRef.current,
        PageSize: query.Take,
        Continuation: '',
      }
      : buildActivityTableSearch(
        activeSearchRef.current,
        query,
      );
    const family = activityQueryFamilyKey(tableSearch, query.Take);
    let tokens = continuationRef.current.get(family);
    if (!tokens) {
      tokens = new Map([[0, '']]);
      continuationRef.current.set(family, tokens);
    }
    const token = tokens.get(page);
    if (page > 0 && !token) {
      setSearchError(
        'This activity page continuation expired. Run the search again.',
      );
      return { Items: [], TotalItemCount: query.Skip, mode: 'replace' };
    }
    const requestSearch = {
      ...tableSearch,
      Continuation: token || '',
    };
    let result;
    if (
      useCachedInitialSearch
      && cached.family === family
      && cached.page === page
    ) {
      result = cached.result;
      firstResultRef.current = null;
    } else {
      result = await executeSearch(requestSearch, { surface: 'grid' });
    }
    if (!result) {
      return { Items: [], TotalItemCount: query.Skip, mode: 'replace' };
    }
    lastGridSearchRef.current = {
      ...requestSearch,
      Continuation: '',
    };
    activeSearchRef.current = lastGridSearchRef.current;
    setSearchDraft((current) => ({
      ...current,
      SortBy: requestSearch.SortBy,
      SortDirection: requestSearch.SortDirection,
      PageSize: requestSearch.PageSize,
    }));
    tokens.set(page + 1, result.Continuation || '');
    setLastResult(result);
    return {
      ...result,
      Items: result.Items || [],
      mode: 'replace',
    };
  }, [executeSearch]);

  const loadDetail = useCallback(async (row) => {
    if (!canLoadDetail) return;
    const source = activitySourceReference(row);
    const request = detailGateRef.current.begin(
      `${clientId}:${currentEnvironment}:activity-detail:${source.SourceKey}:${source.SourceItemId}`,
    );
    setDetail(null);
    setDetailOpen(true);
    setDetailBusy(true);
    setDetailError('');
    try {
      const result = await runActivityOperation(
        managerRef.current,
        CLIENT_ACTIVITY_OPERATIONS.loadDetail,
        buildActivityWorkspaceRequest({
          clientId,
          currentEnvironment,
          source,
        }),
        'Activity detail is unavailable.',
      );
      if (!detailGateRef.current.isCurrent(request)) return;
      setDetail(result);
    } catch (exception) {
      if (detailGateRef.current.isCurrent(request)) {
        setDetailError(
          exception?.message || 'Activity detail is unavailable.',
        );
      }
    } finally {
      if (detailGateRef.current.isCurrent(request)) setDetailBusy(false);
    }
  }, [canLoadDetail, clientId, currentEnvironment]);

  const copyCorrelation = useCallback(async (row) => {
    if (!row?.CorrelationId) {
      showActivityToast('This activity does not have a correlation id.');
      return;
    }
    try {
      await navigator.clipboard.writeText(row.CorrelationId);
      showActivityToast('Correlation id copied.');
    } catch {
      setActionError('The correlation id could not be copied.');
    }
  }, [showActivityToast]);

  const openSourceRecord = useCallback(async (row) => {
    if (!canResolveSourceRoute) return;
    const source = activitySourceReference(row);
    const request = sourceGateRef.current.begin(
      `${clientId}:${currentEnvironment}:activity-source:${source.SourceKey}:${source.SourceItemId}`,
    );
    setSourceLoading(true);
    setActionError('');
    try {
      const route = await runActivityOperation(
        managerRef.current,
        CLIENT_ACTIVITY_OPERATIONS.resolveSourceRoute,
        buildActivityWorkspaceRequest({
          clientId,
          currentEnvironment,
          source,
        }),
        'The source record route is unavailable.',
      );
      if (!sourceGateRef.current.isCurrent(request)) return;
      if (!route?.IsAvailable || !route?.Route) {
        showActivityToast(
          route?.Message || 'This source does not expose a route.',
        );
        return;
      }
      navigate(route.Route);
    } catch (exception) {
      if (sourceGateRef.current.isCurrent(request)) {
        setActionError(
          exception?.message || 'The source record route is unavailable.',
        );
      }
    } finally {
      if (sourceGateRef.current.isCurrent(request)) {
        setSourceLoading(false);
      }
    }
  }, [canResolveSourceRoute, clientId, currentEnvironment, navigate, showActivityToast]);

  const openExport = useCallback(() => {
    if (!canExport) return;
    exportGateRef.current.cancel();
    setExportState({ loading: false, error: '' });
    setExportFormat('');
    setExportOpen(true);
    setActivityToast(null);
  }, [canExport]);

  const closeExport = useCallback(() => {
    exportGateRef.current.cancel();
    setExportState({ loading: false, error: '' });
    setExportFormat('');
    setExportOpen(false);
  }, []);

  const exportSearch = useCallback(async () => {
    if (!canExport || !exportFormat) return;
    const request = exportGateRef.current.begin(
      `${clientId}:${currentEnvironment}:activity-export`,
    );
    setExportState({ loading: true, error: '' });
    setActivityToast(null);
    try {
      const exportItem = await runActivityOperation(
        managerRef.current,
        CLIENT_ACTIVITY_OPERATIONS.exportSearch,
        buildActivityWorkspaceRequest({
          clientId,
          currentEnvironment,
          search: lastGridSearchRef.current,
          exportFormat,
        }),
        'The bounded activity export is unavailable.',
      );
      if (!exportGateRef.current.isCurrent(request)) return;
      const url = URL.createObjectURL(decodeActivityExport(exportItem));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = exportItem.FileName || `client-activity.${exportFormat}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportState({
        loading: false,
        error: '',
      });
      setExportOpen(false);
      setExportFormat('');
      showActivityToast(
        exportItem.IsPartial
          ? 'A bounded partial activity export was downloaded.'
          : 'The bounded activity export was downloaded.',
      );
    } catch (exception) {
      if (exportGateRef.current.isCurrent(request)) {
        setExportState({
          loading: false,
          error:
            exception?.message || 'The bounded activity export is unavailable.',
        });
      }
    } finally {
      if (exportGateRef.current.isCurrent(request)) {
        setExportState((current) => ({
          ...current,
          loading: false,
        }));
      }
    }
  }, [canExport, clientId, currentEnvironment, exportFormat, showActivityToast]);

  const rowActions = useCallback((row) => {
    const actions = [
      ...(canLoadDetail
        ? [{ name: 'View Details', customFunction: loadDetail }]
        : []),
      { name: 'Copy Correlation Id', customFunction: copyCorrelation },
    ];
    if (canResolveSourceRoute && row?.CanOpenSourceRecord) {
      actions.push({
        name: 'Open Source Record',
        customFunction: openSourceRecord,
      });
    }
    return actions;
  }, [canLoadDetail, canResolveSourceRoute, copyCorrelation, loadDetail, openSourceRecord]);

  const searchFields = useMemo(
    () => selectSearchFields(metadata.search),
    [metadata.search],
  );
  const detailFields = useMemo(
    () => metadataFields(metadata.detail),
    [metadata.detail],
  );
  const closeDetail = useCallback(() => {
    detailGateRef.current.cancel();
    setDetailBusy(false);
    setDetailError('');
    setDetailOpen(false);
    setDetail(null);
  }, []);
  const openSearch = useCallback(() => {
    closeDetail();
    setSearchInfoOpen(false);
    setSearchDraft({
      ...activeSearchRef.current,
      Continuation: '',
    });
    setSearchError('');
    setSearchOpen(true);
  }, [closeDetail]);
  const closeSearch = useCallback(() => {
    if (searchBusy) return;
    setSearchOpen(false);
    setSearchDraft({
      ...activeSearchRef.current,
      Continuation: '',
    });
    setSearchError('');
  }, [searchBusy]);
  const appliedSearch = activeSearchRef.current;
  const handleActivityAIUpdate = useCallback((uiAction, item, value) => {
    if (String(uiAction || '').trim().toLowerCase() !== 'filter') return;
    const proposed = normalizeActivityAIFilter(
      value,
      activeSearchRef.current,
    );
    if (!proposed) {
      setSearchError(
        'The AI search proposal contains unsupported Activity fields.',
      );
      return;
    }
    closeDetail();
    setSearchInfoOpen(false);
    setSearchError('');
    setSearchDraft(proposed);
    setSearchOpen(true);
  }, [closeDetail]);
  const activityAIPayload = {
    ActivitySearchRequest: {
      ...appliedSearch,
      Continuation: '',
    },
    CheckedAtUtc: lastResult?.CheckedAtUtc || null,
    IsPartial: Boolean(lastResult?.IsPartial),
  };
  const commandBarActions = [
    {
      key: 'refresh-activity',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: pageLoading || searchBusy || !lastResult,
      onClick: refreshSearch,
    },
    {
      key: 'search-activity',
      label: 'Search',
      iconKey: 'search',
      disabled: pageLoading || searchBusy || !metadata.search,
      onClick: openSearch,
    },
    {
      key: 'clear-activity-search',
      label: 'Clear',
      iconKey: 'clearall',
      disabled: pageLoading || searchBusy,
      onClick: clearSearch,
    },
    ...(canExport ? [{
      key: 'export-activity',
      label: 'Export',
      iconKey: 'export',
      disabled: pageLoading || searchBusy || exportState.loading || !lastResult,
      onClick: openExport,
    }] : []),
  ];

  return (
    <AIPanel
      open
      chatEnabled={canUseAI}
      presentation="screen-workspace"
      workspaceHeader={workspaceHeader}
      operationName={canUseAI
        ? CLIENT_ACTIVITY_OPERATIONS.aiQuestion
        : undefined}
      referenceId={`${clientId}:${currentEnvironment}`}
      referenceName="ClientActivity"
      params={{
        clientId,
        currentEnvironment,
      }}
      payload={activityAIPayload}
      onUpdatePanel={handleActivityAIUpdate}
      chatPane="right"
      chatPaneCollapseMode="auto"
      chatPaneStartOpen={searchOpen || detailOpen ? false : null}
      aiProcessingMode="Direct"
      singleSessionMode
      planningPreviewEnabled={false}
      previewPanelEnabled={false}
      splashMessage="Ask AI to explain the current activity results or prepare a search filter."
      introMessage="I can analyze only the current bounded, server-authorized Activity & Logs evidence. Any proposed filter opens Search for your review and does not run automatically."
      minLeft={360}
      minRight={360}
      panelStorageKey={null}
    >
      <div className="resource-workspace-overview command-entry command-activity">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Activity and Logs actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      {activityToast ? (
        <ToastNotifications
          key={activityToast.id}
          position="top"
          message={activityToast.message}
        />
      ) : null}
      <ClientActivityState
        loading={pageLoading}
        error={pageError}
      />

      {lastResult?.IsPartial ? (
        <ResourceStateControl
          title="Partial results"
          message="Some activity sources could not be searched. Available results are still shown."
          tone="warning"
          compact
          role="note"
        />
      ) : null}

      {metadata.activity ? (
        <section className="resource-workspace-panel command-activity__results">
          <div className="command-activity__results-heading">
            <h3>Activity &amp; Log Results</h3>
            <ButtonControl
              type="icon"
              className="command-activity__results-info"
              title="Activity search filters and scope"
              aria-label="Activity search filters and scope"
              onClick={() => setSearchInfoOpen(true)}
            >
              <IconControl
                iconKey="filterlist"
                title="Activity search filters and scope"
                width="1rem"
                height="1rem"
              />
            </ButtonControl>
          </div>
          {sourceLoading ? (
            <ClientActivityState
              loading={sourceLoading}
              loadingText="Resolving the activity source route..."
            />
          ) : null}
          <TableControl
            key={`client-activity-${gridGeneration}`}
            data={[]}
            defaultNumRows={Number(activeSearchRef.current.PageSize || 50)}
            disableTableTitle
            enableMultiSelect={false}
            enableSidePanelEdit={false}
            filters={[]}
            headerFeatures={['search', 'tableStyles', 'columnSettings']}
            enableTableDataSettings={false}
            loadingState={pageLoading || searchBusy}
            metadata={[metadata.activity]}
            mgr={null}
            onServerSideQuery={queryGrid}
            customQuickViewList={rowActions}
          />
        </section>
      ) : null}

      <DialogControl
        openDialog={searchInfoOpen}
        onCancel={() => setSearchInfoOpen(false)}
        onOk={() => setSearchInfoOpen(false)}
        title="Activity search information"
        okText="Close"
        hideCancel
        hideCancelButton
      >
        <div className="command-activity__search-info-dialog">
          <p>Current scope and criteria for the activity results.</p>
          <dl className="resource-workspace-facts command-activity__summary">
            <div>
              <dt>Environment</dt>
              <dd>{currentEnvironment}</dd>
            </div>
            <div>
              <dt>Search coverage</dt>
              <dd>
                {lastResult
                  ? (lastResult.IsPartial ? 'Partial results' : 'Available')
                  : 'Not checked'}
              </dd>
            </div>
            <div>
              <dt>Last checked</dt>
              <dd>
                {lastResult?.CheckedAtUtc
                  ? formatActivityDateTime(lastResult.CheckedAtUtc)
                  : 'Not yet'}
              </dd>
            </div>
            <div className="command-activity__summary-range">
              <dt>Time range</dt>
              <dd>
                {formatActivityDateTime(appliedSearch.FromUtc)} —{' '}
                {formatActivityDateTime(appliedSearch.ToUtc)}
              </dd>
            </div>
            <div>
              <dt>Sort and page</dt>
              <dd>{activitySortSummary(appliedSearch)}</dd>
            </div>
            <div className="command-activity__summary-filters">
              <dt>Applied filters</dt>
              <dd>{activityFilterSummary(appliedSearch)}</dd>
            </div>
          </dl>
        </div>
      </DialogControl>

      <DialogControl
        openDialog={exportOpen}
        onCancel={closeExport}
        onX={closeExport}
        onOk={exportSearch}
        title="Export Activity & Logs"
        okText={exportState.loading ? 'Exporting...' : 'Export'}
        cancelText="Cancel"
        disableOk={exportState.loading || !exportFormat}
      >
        <div className="command-activity__export-dialog">
          <p>Choose the format for the bounded activity results.</p>
          <div className="command-activity__export-formats">
            <RadioButtonListControl
              metadata={ACTIVITY_EXPORT_FORMAT_METADATA}
              value={exportFormat}
              disabled={exportState.loading}
              onBlur={() => {}}
              onChange={(event) => {
                setExportFormat(event.target.value);
                setExportState({ loading: false, error: '' });
              }}
            />
          </div>
          {exportState.error ? (
            <p className="command-activity__export-error" role="alert">
              {exportState.error}
            </p>
          ) : null}
        </div>
      </DialogControl>

      <DialogControl
        openDialog={Boolean(searchError)}
        onCancel={() => setSearchError('')}
        onX={() => setSearchError('')}
        onOk={() => setSearchError('')}
        title="Activity search failed"
        okText="Close"
        excludeCancel
      >
        <ConvokeDialogMessage message={searchError} tone="error" />
      </DialogControl>

      <DialogControl
        openDialog={Boolean(actionError)}
        onCancel={() => setActionError('')}
        onX={() => setActionError('')}
        onOk={() => setActionError('')}
        title="Activity action failed"
        okText="Close"
        excludeCancel
      >
        <ConvokeDialogMessage message={actionError} tone="error" />
      </DialogControl>

      <SidePanelFormControl
        open={searchOpen}
        title="Search Activity & Logs"
        metadata={metadata.search}
        formFields={searchFields}
        itemData={searchDraft}
        statusContent={searchBusy ? (
          <ClientActivityState
            loading={searchBusy}
          />
        ) : null}
        onChange={(value, fieldName, nextItem) => {
          if (nextItem) {
            setSearchDraft(nextItem);
            return;
          }
          setSearchDraft((current) => ({
            ...current,
            [fieldName]: value,
          }));
        }}
        onSave={runBoundedSearch}
        onCancel={closeSearch}
        onUpdateSidePanel={() => {}}
        saveButtonText={searchBusy ? 'Searching...' : 'Apply Search'}
        setOpenEditSidePanel={(open) => {
          if (open) openSearch();
          else closeSearch();
        }}
        setItemData={(item) => {
          if (item) setSearchDraft(item);
        }}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={detailOpen}
        title="Client Activity Detail"
        metadata={metadata.detail}
        formFields={detailFields}
        itemData={detail}
        statusContent={(detailBusy || detailError) ? (
          <ClientActivityState
            loading={detailBusy}
            loadingText="Loading activity detail..."
            error={detailError}
          />
        ) : null}
        onSave={() => {}}
        onCancel={closeDetail}
        onUpdateSidePanel={() => {}}
        setOpenEditSidePanel={(open) => {
          if (open) setDetailOpen(true);
          else closeDetail();
        }}
        setItemData={setDetail}
        viewOnly
      />
      </div>
    </AIPanel>
  );
};

export default ClientActivityScreen;
